import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ImageSlotManager, slotsFromExistingImages, buildSlotPayload } from '../../components/ImageSlotManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'furniture',
    price: '',
    description: '',
    inStock: true,
    featured: false,
  });
  const [slots, setSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        category: product.category,
        price: product.price,
        description: product.description,
        inStock: product.inStock,
        featured: product.featured,
      });
      const existing = product.images && product.images.length > 0
        ? product.images
        : (product.image ? [product.image] : []);
      // Use multi overlays if present, else fall back to legacy single overlay on slot 0
      let overlays = product.imageOverlays && product.imageOverlays.length > 0
        ? product.imageOverlays
        : [];
      if (overlays.length === 0 && product.imageOverlay) {
        overlays = [product.imageOverlay];
      }
      setSlots(slotsFromExistingImages(existing, overlays));
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: 'furniture',
        price: '',
        description: '',
        inStock: true,
        featured: false,
      });
      setSlots([]);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setSlots([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const token = localStorage.getItem('admin_token');
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('category', formData.category);
      fd.append('price', formData.price);
      fd.append('description', formData.description);
      fd.append('inStock', formData.inStock);
      fd.append('featured', formData.featured);
      // Keep legacy single overlay in sync with slot[0]'s overlay for backward compat
      fd.append('imageOverlay', slots[0]?.overlay || '');
      const { hasFiles } = buildSlotPayload(slots, fd);

      const config = {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: hasFiles ? (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        } : undefined,
      };

      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editingProduct.id}`, fd, config);
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/products`, fd, config);
        toast.success('Product created successfully!');
      }
      handleCloseModal();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (productId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product moved to Trash');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setPendingDeleteId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-gray-600">Loading products...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Products Management</h1>
          <Button
            onClick={() => handleOpenModal()}
            className="text-white font-semibold"
            style={{ backgroundColor: '#00CED1' }}
            data-testid="admin-products-add-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Product
          </Button>
        </div>

        {products.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No products yet. Click "Add New Product" to create one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden" data-testid={`admin-products-card-${product.id}`}>
                <div className="h-48 bg-gray-100 relative">
                  {product.image ? (
                    <img
                      src={resolveUrl(product.image)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                  {(product.images || []).length > 1 && (
                    <span
                      className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded"
                      title={`${product.images.length} images`}
                    >
                      +{product.images.length - 1} more
                    </span>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-lg">{product.name}</h3>
                    <span className="text-lg font-bold" style={{ color: '#00CED1' }}>
                      R{product.price.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 capitalize">{product.category}</p>
                  <p className="text-sm text-gray-700 mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex space-x-2 mb-4">
                    <span className={`text-xs px-2 py-1 rounded ${product.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {product.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                    {product.featured && (
                      <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenModal(product)}
                      className="flex-1" data-testid={`admin-products-edit-${product.id}`}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPendingDeleteId(product.id)}
                      className="flex-1 text-red-600 hover:text-red-700" data-testid={`admin-products-delete-${product.id}`}>
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Product Name *</Label>
                <Input id="name" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="decor">Decor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">Price (R) *</Label>
                <Input id="price" type="number" step="0.01" value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea id="description" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3} required />
              </div>

              <ImageSlotManager
                slots={slots}
                onSlotsChange={setSlots}
                testIdPrefix="admin-products"
                isUploading={submitting}
                uploadProgress={uploadProgress}
              />

              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.inStock}
                    onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })} />
                  <span className="text-sm">In Stock</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} />
                  <span className="text-sm">Featured</span>
                </label>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={submitting} className="flex-1 text-white font-semibold"
                  style={{ backgroundColor: '#00CED1' }} data-testid="admin-products-submit">
                  {submitting
                    ? (uploadProgress > 0 && uploadProgress < 100 ? `Uploading ${uploadProgress}%…` : 'Saving...')
                    : (editingProduct ? 'Update Product' : 'Create Product')}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal}
                  className="flex-1" data-testid="admin-products-cancel">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={!!pendingDeleteId}
          title="Move this product to Trash?"
          description="The product will be moved to Trash. You can restore it from there."
          confirmLabel="Delete"
          destructive
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={() => handleDelete(pendingDeleteId)}
          testId="admin-products-confirm-delete"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
